import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
} from 'react';

import { useDebounce } from '@mint-lib/hooks';
import styled from '@mint-lib/styled';
import { getRouteApi } from '@mint/routing';
import { useDebounce as useDebounce2 } from 'use-debounce';

import { Box } from '@myn/mui';

import VersionsHeader from '../../features/VersionsHeader/VersionsHeader.jsx';
import VersionsRow from '../../features/VersionsRow/VersionsRow.jsx';
import { useCurrentVersionId } from '../../hooks/useGetBuyingPlanData.js';
import { useGetVersionChannelMixData } from '../../hooks/useGetVersionChannelMixData.js';
import { useGetVersionListData } from '../../hooks/useGetVersionListData.js';
import { useSelectedBuyingPlanId } from '../../hooks/useSelectedBuyingPlanId.js';
import {
  Version,
  VersionsEventEmitterAction,
  VersionView,
} from '../../types/versions.js';
import eventEmitter from '../../utils/eventEmitter.js';
import { useCreateNewVersion } from '../../utils/versionPageUtils.js';
import DeleteModal from './DeleteModal.jsx';
import VersionsPageNoContent from './VersionsPageNoContent.jsx';
import VersionViewComponent from './VersionView.jsx';

export type Props = {
  handleDrawerClose: () => void;
};

const DEBOUNCE_DELAY = 500;

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

const VersionsPage: FC<Props> = ({ handleDrawerClose }) => {
  const navigate = routeApi.useNavigate();

  const [selectedVersionList, setSelectedVersionList] = useState<number[]>([]);
  const [isSelectedListPopulated, setIsSelectedListPopulated] = useState(false);

  const selectedPlanId = useSelectedBuyingPlanId();
  const currentVersionId = useCurrentVersionId();

  const [searchValue, setSearchValue] = useState<string>('');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentView, setCurrentView] = useState<VersionView>(VersionView.Mix);
  const [openedVersionId, setOpenedVersionId] = useState<number | null>(null);

  const searchValueDebounced = useDebounce(searchValue.trim(), DEBOUNCE_DELAY);

  const { data: { versions } = {}, isLoading: isVersionsForMixViewLoading } =
    useGetVersionListData({
      versionName: searchValueDebounced || undefined,
    });

  const { isLoading: isChannelMixDataLoading } = useGetVersionChannelMixData({
    versionId: versions?.[0]?.id ?? null,
  });

  const currentVersion = versions?.find((v) => v.isCurrent);

  const versionOptions = useMemo(() => {
    if (!versions) {
      return [];
    }
    return versions.map((item) => {
      return { label: item.name, value: item.id };
    });
  }, [versions]);

  const filteredVersionList = useMemo(() => {
    if (!versions) {
      return [];
    }

    if (!selectedVersionList.length) {
      return versions;
    }

    return versions.filter((v) => {
      return selectedVersionList.includes(v.id);
    });
  }, [versions, selectedVersionList]);

  const { handleCreateNewVersion } = useCreateNewVersion();

  const createNewVersion = useCallback(
    async (id: number) => {
      handleDrawerClose();
      await handleCreateNewVersion({
        versionId: id,
      });
      navigate({ search: {} });
    },
    [selectedPlanId],
  );

  const handleOpenVersion = async (item: Version) => {
    handleDrawerClose();
    setOpenedVersionId(item.id);
  };

  const handleOpenDeleteModalClick = (id: number) => {
    setDeleteId(id);
  };

  const handleSearchVersion = async (versionSearchName: string) => {
    setSearchValue(versionSearchName);
  };

  const filteredTableVersions = useMemo(() => {
    if (!versions) {
      return [];
    }

    return versions.reduce<{ id: number }[]>((acc, version) => {
      const preparedVersionName = version.name.toLowerCase().trim();
      const preparedSearchValue = searchValue.toLowerCase().trim();
      const isSelected = selectedVersionList.length
        ? selectedVersionList.includes(version.id)
        : true;

      const isValid =
        isSelected && preparedVersionName.includes(preparedSearchValue);

      if (!isValid) {
        return acc;
      }

      acc.push({ id: version.id });
      return acc;
    }, []);
  }, [selectedVersionList, searchValue, versions]);

  const [debouncedFilteredTableVersions] = useDebounce2(
    filteredTableVersions,
    DEBOUNCE_DELAY,
  );

  useEffect(() => {
    const eventListeners = [
      {
        event: VersionsEventEmitterAction.DUPLICATE_VERSION,
        handler: createNewVersion,
      },
      {
        event: VersionsEventEmitterAction.DELETE_VERSION,
        handler: handleOpenDeleteModalClick,
      },
      {
        event: VersionsEventEmitterAction.OPEN_VERSION,
        handler: handleOpenVersion,
      },
    ];

    eventListeners.forEach(({ event, handler }) => {
      eventEmitter.addListener(event, handler);
    });

    return () => {
      eventListeners.forEach(({ event, handler }) => {
        eventEmitter.removeListener(event, handler);
      });
    };
  }, []);

  useEffect(() => {
    setIsSelectedListPopulated(false);
  }, [searchValueDebounced]);

  useEffect(() => {
    const skipListPopulation =
      isSelectedListPopulated || !versionOptions.length;

    if (skipListPopulation) {
      return;
    }

    setSelectedVersionList(versionOptions.map((option) => option.value));
    setIsSelectedListPopulated(true);
  }, [selectedVersionList, versionOptions, isSelectedListPopulated]);

  return (
    <Wrapper>
      <VersionsHeader
        searchValue={searchValue}
        onSearchChange={handleSearchVersion}
        selectedVersions={selectedVersionList}
        versionOptions={versionOptions}
        onSelectVersion={setSelectedVersionList}
        onClickVersionView={setCurrentView}
        currentView={currentView}
      />

      <VersionViewComponent
        currentView={currentView}
        isLoading={isVersionsForMixViewLoading || isChannelMixDataLoading}
        versions={debouncedFilteredTableVersions}
      >
        {filteredVersionList?.length ? (
          <VersionsMixView>
            <Box sx={{ display: 'grid', gap: 3 }}>
              {filteredVersionList.map((version) => (
                <VersionsRow
                  key={version.id}
                  item={version}
                  versions={versions}
                  openedVersionId={openedVersionId ?? currentVersionId}
                  onNewVersionCreate={createNewVersion}
                  onOpenVersion={handleOpenVersion}
                  onClickDelete={handleOpenDeleteModalClick}
                />
              ))}
            </Box>
          </VersionsMixView>
        ) : (
          <VersionsPageNoContent search={searchValue} />
        )}
      </VersionViewComponent>
      <DeleteModal
        versionId={deleteId}
        versionName={currentVersion?.name ?? ''}
        onClose={() => setDeleteId(null)}
      />
    </Wrapper>
  );
};

const Wrapper = styled('div')`
  width: 1320px;
  height: 100%;
  display: flex;
  flex-flow: column;
`;

const VersionsMixView = styled('div')`
  height: ${window.innerHeight - 250}px;
  overflow-y: auto;
  margin: ${({ theme }) => theme.spacing(-0.5)};
  padding: ${({ theme }) => theme.spacing(0.5)};
`;

export default memo(VersionsPage);
